import type { ServerLoadEvent } from '@sveltejs/kit';
import { error } from '@sveltejs/kit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { GetTalksGalleryDocument } from '../../../../queries/__generated__/getTalksGallery.generated';
import { getTalksGalleryFilterSectionsGql } from '../getTalksGalleryFilterSectionsGql/getTalksGalleryFilterSectionsGql';
import { Config } from '#lib/constants/config.js';
import { SiteNames } from '#lib/constants/site-names.js';
import { gqlClient } from '#lib/gqlClient.js';
import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
import { TalksGallerySort } from '#lib/websites/talks/constants/sorting.js';
import { GetTalksGallerySeoDocument } from '#lib/websites/talks/queries/__generated__/getTalksGallerySeo.generated.js';
import { GetTalksFilterOptionsDocument } from '#lib/websites/talks/queries/gallery/__generated__/getTalksFilterOptions.generated.js';
import type { FullSeo } from '$global/features/seo/types';

export const TALKS_GALLERY_LIMIT = 12;

export const getTalksGalleryBaseVariables = (date: string) => ({
	limit: TALKS_GALLERY_LIMIT,
	offset: 0,
	sort: [TalksGallerySort.Newest],
	filter: {
		_and: [
			{
				_or: [
					{
						displaying_until: {
							_gte: date,
						},
					},
					{ displaying_until: { _null: true } },
				],
			},
		],
	},
});

// The GraphQL version, kept while FeatureFlags.RestPages['/talks/gallery'] is off.
// Keeps the GraphQL shape: this data feeds the GraphQL-keyed query cache, and the
// component reads it through this loader's type while the flag is off; deleted
// with the flag (rest-migration/feature-flags.md).
export const talksGalleryPageServerLoadGql = async ({
	parent,
}: ServerLoadEvent) => {
	const parentData = await parent();
	dayjs.extend(utc);

	try {
		const filterOptions = await gqlClient.request(
			GetTalksFilterOptionsDocument
		);

		const currentDate = dayjs().utc().toISOString();
		const pageSeo = await gqlClient.request(GetTalksGallerySeoDocument);

		const seo = {
			...pageSeo.Page_SEO?.talks_gallery,
			site: SiteNames.HeniEditions,
			url: `${Config.Domain}${EditionsRoutes.Gallery}`,
		} as FullSeo;

		if (!filterOptions) {
			error(404, 'Not found');
		}

		const filterSections = getTalksGalleryFilterSectionsGql({ filterOptions });

		const talks = await gqlClient.request(
			GetTalksGalleryDocument,
			getTalksGalleryBaseVariables(currentDate)
		);

		const data = {
			...parentData,
			currentDate,
			filterSections,
			talks,
			seo,
		};

		return data;
	} catch {
		error(404, 'Not found');
	}
};
